/**
 * Subscription Modal
 *
 * Permite al SUPER_ADMIN gestionar la suscripcion de una IPS:
 * - Ver el estado actual (plan, status, fechas)
 * - Asignar plan (PILOTO o NORMAL)
 * - Suspender / reactivar acceso
 * - Extender el trial
 * - Registrar pago manual
 *
 * Diseñado como modal autocontenido reutilizable desde InstitutionsPage.
 */

import { useState } from 'react'
import { CreditCard, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import {
  useInstitutionSubscription,
  useAssignSubscription,
  useSuspendSubscription,
  useReactivateSubscription,
  useExtendTrial,
  useRecordPayment,
  usePlans,
} from '@/application/subscriptions/useSubscriptions'
import {
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_COLORS,
  type SubscriptionStatus,
} from '@/domain/subscription/types'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import type { Institution } from '@/domain/institution/types'

interface SubscriptionModalProps {
  institution: Institution | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

type ActiveAction =
  | 'idle'
  | 'assign'
  | 'suspend'
  | 'reactivate'
  | 'extend'
  | 'payment'

const formatDate = (iso: string | null): string => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatCop = (amount: number): string =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(amount)

const SubscriptionStatusBadge = ({ status }: { status: SubscriptionStatus }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SUBSCRIPTION_STATUS_COLORS[status]}`}
  >
    {SUBSCRIPTION_STATUS_LABELS[status]}
  </span>
)

export function SubscriptionModal({
  institution,
  open,
  onOpenChange,
}: SubscriptionModalProps) {
  const institutionId = institution?.id ?? null
  const [activeAction, setActiveAction] = useState<ActiveAction>('idle')

  const subscriptionQuery = useInstitutionSubscription(institutionId)
  const plansQuery = usePlans()

  const assignMutation = useAssignSubscription()
  const suspendMutation = useSuspendSubscription(institutionId ?? '')
  const reactivateMutation = useReactivateSubscription(institutionId ?? '')
  const extendMutation = useExtendTrial(institutionId ?? '')
  const paymentMutation = useRecordPayment(institutionId ?? '')

  const subscription = subscriptionQuery.data?.subscription
  const noSubscription =
    subscriptionQuery.isError &&
    (subscriptionQuery.error as { statusCode?: number; status?: number })?.statusCode === 404

  const handleClose = () => {
    setActiveAction('idle')
    onOpenChange(false)
  }

  if (!institution) return null

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Suscripcion de {institution.name}
          </DialogTitle>
          <DialogDescription>
            Gestiona el plan, los pagos y el acceso operacional de la IPS.
          </DialogDescription>
        </DialogHeader>

        {subscriptionQuery.isLoading || plansQuery.isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : noSubscription || !subscription ? (
          <NoSubscriptionView
            institutionId={institution.id}
            plans={plansQuery.data?.plans ?? []}
            assigning={assignMutation.isPending}
            onAssign={(planCode, trialDaysOverride, notes) => {
              assignMutation.mutate(
                {
                  institutionId: institution.id,
                  data: { planCode, trialDaysOverride, notes },
                },
                {
                  onSuccess: () => {
                    toast.success(`Plan ${planCode} asignado`)
                    setActiveAction('idle')
                  },
                  onError: (err) => toast.error(getErrorMessage(err)),
                }
              )
            }}
          />
        ) : (
          <div className="space-y-4 mt-2">
            <div className="rounded-lg border border-[var(--border)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
                    Plan asignado
                  </p>
                  <p className="text-lg font-semibold">{subscription.plan.name}</p>
                  <p className="text-xs text-[var(--foreground-muted)]">
                    {subscription.plan.code} · {formatCop(subscription.plan.monthlyPriceCop)}/mes
                  </p>
                </div>
                <SubscriptionStatusBadge status={subscription.status} />
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[var(--foreground-muted)]">Inicio</p>
                  <p>{formatDate(subscription.startsAt)}</p>
                </div>
                {subscription.trialEndsAt && (
                  <div>
                    <p className="text-[var(--foreground-muted)]">Fin del trial</p>
                    <p>{formatDate(subscription.trialEndsAt)}</p>
                  </div>
                )}
                {subscription.expiresAt && (
                  <div>
                    <p className="text-[var(--foreground-muted)]">Vencimiento</p>
                    <p>{formatDate(subscription.expiresAt)}</p>
                  </div>
                )}
                {subscription.gracePeriodEndsAt && (
                  <div>
                    <p className="text-[var(--foreground-muted)]">Fin cortesia</p>
                    <p>{formatDate(subscription.gracePeriodEndsAt)}</p>
                  </div>
                )}
                {subscription.suspendedAt && (
                  <div className="col-span-2">
                    <p className="text-[var(--foreground-muted)]">Suspendida</p>
                    <p className="text-red-700 dark:text-red-300">
                      {formatDate(subscription.suspendedAt)} —{' '}
                      {subscription.suspendedReason ?? 'sin motivo'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {subscription.status === 'TRIAL' && (
                <Button variant="outline" size="sm" onClick={() => setActiveAction('extend')}>
                  Extender trial
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setActiveAction('payment')}>
                Registrar pago
              </Button>
              {subscription.status !== 'SUSPENDED' &&
                subscription.status !== 'CANCELLED' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveAction('suspend')}
                    className="text-red-700 dark:text-red-300"
                  >
                    Suspender
                  </Button>
                )}
              {subscription.status === 'SUSPENDED' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    reactivateMutation.mutate(subscription.id, {
                      onSuccess: () => toast.success('Suscripcion reactivada'),
                      onError: (err) => toast.error(getErrorMessage(err)),
                    })
                  }}
                  disabled={reactivateMutation.isPending}
                >
                  {reactivateMutation.isPending ? 'Reactivando...' : 'Reactivar'}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setActiveAction('assign')}>
                Cambiar plan
              </Button>
            </div>

            {/* Inline forms */}
            {activeAction === 'extend' && (
              <ExtendTrialForm
                pending={extendMutation.isPending}
                onCancel={() => setActiveAction('idle')}
                onSubmit={(extraDays, reason) => {
                  extendMutation.mutate(
                    {
                      subscriptionId: subscription.id,
                      data: { extraDays, reason },
                    },
                    {
                      onSuccess: () => {
                        toast.success('Trial extendido')
                        setActiveAction('idle')
                      },
                      onError: (err) => toast.error(getErrorMessage(err)),
                    }
                  )
                }}
              />
            )}

            {activeAction === 'suspend' && (
              <SuspendForm
                pending={suspendMutation.isPending}
                onCancel={() => setActiveAction('idle')}
                onSubmit={(reason) => {
                  suspendMutation.mutate(
                    { subscriptionId: subscription.id, data: { reason } },
                    {
                      onSuccess: () => {
                        toast.success('Suscripcion suspendida')
                        setActiveAction('idle')
                      },
                      onError: (err) => toast.error(getErrorMessage(err)),
                    }
                  )
                }}
              />
            )}

            {activeAction === 'payment' && (
              <RecordPaymentForm
                subscriptionId={subscription.id}
                defaultAmount={subscription.plan.monthlyPriceCop}
                pending={paymentMutation.isPending}
                onCancel={() => setActiveAction('idle')}
                onSubmit={(data) => {
                  paymentMutation.mutate(data, {
                    onSuccess: () => {
                      toast.success('Pago registrado')
                      setActiveAction('idle')
                    },
                    onError: (err) => toast.error(getErrorMessage(err)),
                  })
                }}
              />
            )}

            {activeAction === 'assign' && (
              <AssignSubscriptionForm
                plans={plansQuery.data?.plans ?? []}
                pending={assignMutation.isPending}
                onCancel={() => setActiveAction('idle')}
                onSubmit={(planCode, trialDaysOverride, notes) => {
                  assignMutation.mutate(
                    {
                      institutionId: institution.id,
                      data: { planCode, trialDaysOverride, notes },
                    },
                    {
                      onSuccess: () => {
                        toast.success(`Plan ${planCode} asignado`)
                        setActiveAction('idle')
                      },
                      onError: (err) => toast.error(getErrorMessage(err)),
                    }
                  )
                }}
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Subforms
// =============================================================================

interface NoSubscriptionViewProps {
  institutionId: string
  plans: Array<{ code: string; name: string; isPilot: boolean; trialDays: number }>
  assigning: boolean
  onAssign: (planCode: string, trialDaysOverride?: number, notes?: string) => void
}

function NoSubscriptionView({ plans, assigning, onAssign }: NoSubscriptionViewProps) {
  const [planCode, setPlanCode] = useState(plans[0]?.code ?? '')
  const [trialDays, setTrialDays] = useState<string>('')
  const [notes, setNotes] = useState('')

  return (
    <div className="space-y-3 mt-2">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 dark:bg-blue-950/40 p-3 text-sm">
        <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
        <p className="text-blue-900 dark:text-blue-100">
          Esta institucion no tiene suscripcion. Asigna un plan para habilitar el acceso.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Plan</label>
        <select
          className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
          value={planCode}
          onChange={(e) => setPlanCode(e.target.value)}
        >
          {plans.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name} {p.isPilot ? `(trial ${p.trialDays}d)` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">
          Dias de trial (opcional, override)
        </label>
        <Input
          type="number"
          min={0}
          max={365}
          placeholder="Usa el default del plan"
          value={trialDays}
          onChange={(e) => setTrialDays(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Notas (opcional)</label>
        <Input
          placeholder="Razon del trial extendido, contacto, etc."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <Button
        onClick={() =>
          onAssign(
            planCode,
            trialDays ? Number(trialDays) : undefined,
            notes || undefined
          )
        }
        disabled={assigning || !planCode}
      >
        {assigning ? 'Asignando...' : 'Asignar plan'}
      </Button>
    </div>
  )
}

interface AssignFormProps {
  plans: Array<{ code: string; name: string; isPilot: boolean; trialDays: number }>
  pending: boolean
  onCancel: () => void
  onSubmit: (planCode: string, trialDaysOverride?: number, notes?: string) => void
}

function AssignSubscriptionForm({ plans, pending, onCancel, onSubmit }: AssignFormProps) {
  const [planCode, setPlanCode] = useState(plans[0]?.code ?? '')
  const [trialDays, setTrialDays] = useState('')
  const [notes, setNotes] = useState('')

  return (
    <div className="rounded-md border border-[var(--border)] p-3 space-y-3">
      <p className="text-sm font-semibold">Asignar nuevo plan</p>
      <p className="text-xs text-[var(--foreground-muted)]">
        Esto cancelara la suscripcion vigente y creara una nueva.
      </p>
      <select
        className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
        value={planCode}
        onChange={(e) => setPlanCode(e.target.value)}
      >
        {plans.map((p) => (
          <option key={p.code} value={p.code}>
            {p.name} {p.isPilot ? `(trial ${p.trialDays}d)` : ''}
          </option>
        ))}
      </select>
      <Input
        type="number"
        min={0}
        max={365}
        placeholder="Dias de trial (opcional)"
        value={trialDays}
        onChange={(e) => setTrialDays(e.target.value)}
      />
      <Input
        placeholder="Notas (opcional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() =>
            onSubmit(
              planCode,
              trialDays ? Number(trialDays) : undefined,
              notes || undefined
            )
          }
          disabled={pending || !planCode}
        >
          {pending ? 'Asignando...' : 'Confirmar'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}

interface ExtendFormProps {
  pending: boolean
  onCancel: () => void
  onSubmit: (extraDays: number, reason: string) => void
}

function ExtendTrialForm({ pending, onCancel, onSubmit }: ExtendFormProps) {
  const [days, setDays] = useState('30')
  const [reason, setReason] = useState('')

  const valid = Number(days) > 0 && reason.trim().length >= 3

  return (
    <div className="rounded-md border border-[var(--border)] p-3 space-y-3">
      <p className="text-sm font-semibold">Extender periodo de prueba</p>
      <Input
        type="number"
        min={1}
        max={365}
        placeholder="Dias adicionales"
        value={days}
        onChange={(e) => setDays(e.target.value)}
      />
      <Input
        placeholder="Razon (visible en auditoria)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => onSubmit(Number(days), reason.trim())}
          disabled={pending || !valid}
        >
          {pending ? 'Extendiendo...' : 'Extender'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}

interface SuspendFormProps {
  pending: boolean
  onCancel: () => void
  onSubmit: (reason: string) => void
}

function SuspendForm({ pending, onCancel, onSubmit }: SuspendFormProps) {
  const [reason, setReason] = useState('')
  const valid = reason.trim().length >= 3

  return (
    <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 space-y-3">
      <p className="text-sm font-semibold text-red-900 dark:text-red-100">Suspender acceso</p>
      <p className="text-xs text-red-800 dark:text-red-200">
        La IPS no podra operar hasta ser reactivada o registrar un pago.
      </p>
      <Input
        placeholder="Razon (visible en auditoria)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => onSubmit(reason.trim())}
          disabled={pending || !valid}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          {pending ? 'Suspendiendo...' : 'Suspender'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}

interface PaymentFormProps {
  subscriptionId: string
  defaultAmount: number
  pending: boolean
  onCancel: () => void
  onSubmit: (data: {
    subscriptionId: string
    amountCop: number
    paidAt: string
    periodStart: string
    periodEnd: string
    paymentMethod?: string
    reference?: string
  }) => void
}

function RecordPaymentForm({
  subscriptionId,
  defaultAmount,
  pending,
  onCancel,
  onSubmit,
}: PaymentFormProps) {
  const today = new Date().toISOString().slice(0, 10)
  const inOneMonth = (() => {
    const d = new Date()
    d.setMonth(d.getMonth() + 1)
    return d.toISOString().slice(0, 10)
  })()

  const [amount, setAmount] = useState(String(defaultAmount || 0))
  const [paidAt, setPaidAt] = useState(today)
  const [periodStart, setPeriodStart] = useState(today)
  const [periodEnd, setPeriodEnd] = useState(inOneMonth)
  const [method, setMethod] = useState('')
  const [reference, setReference] = useState('')

  const valid =
    Number(amount) > 0 &&
    paidAt &&
    periodStart &&
    periodEnd &&
    new Date(periodEnd) > new Date(periodStart)

  return (
    <div className="rounded-md border border-[var(--border)] p-3 space-y-3">
      <p className="text-sm font-semibold flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
        Registrar pago
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-[var(--foreground-muted)]">Monto (COP)</label>
          <Input
            type="number"
            min={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-[var(--foreground-muted)]">Fecha de pago</label>
          <Input
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-[var(--foreground-muted)]">Periodo desde</label>
          <Input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-[var(--foreground-muted)]">Periodo hasta</label>
          <Input
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-[var(--foreground-muted)]">Metodo (opcional)</label>
          <Input
            placeholder="Transferencia, PSE, efectivo"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-[var(--foreground-muted)]">Referencia (opcional)</label>
          <Input
            placeholder="No. transaccion / factura"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() =>
            onSubmit({
              subscriptionId,
              amountCop: Number(amount),
              paidAt: new Date(paidAt).toISOString(),
              periodStart: new Date(periodStart).toISOString(),
              periodEnd: new Date(periodEnd).toISOString(),
              paymentMethod: method || undefined,
              reference: reference || undefined,
            })
          }
          disabled={pending || !valid}
        >
          {pending ? 'Registrando...' : 'Registrar pago'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}

export default SubscriptionModal
